import { Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useFavorites } from "@/context/favorites-context"
import { cn } from "@/lib/utils"

interface FavoriteStarProps {
  toolId: string
  size?: "sm" | "md" | "lg"
  variant?: "button" | "icon"
  className?: string
  onClick?: (e: React.MouseEvent) => void
}

export function FavoriteStar({
  toolId,
  size = "md",
  variant = "button",
  className,
  onClick
}: FavoriteStarProps) {
  const { toggleFavorite, isFavorite } = useFavorites()
  const isToolFavorite = isFavorite(toolId)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent click handlers
    toggleFavorite(toolId)
    onClick?.(e)
  }

  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  }

  const buttonSizeClasses = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-10 w-10"
  }

  if (variant === "icon") {
    return (
      <Star
        className={cn(
          sizeClasses[size],
          "cursor-pointer transition-all duration-200",
          isToolFavorite
            ? "fill-yellow-400 text-yellow-400 hover:fill-yellow-500 hover:text-yellow-500"
            : "text-muted-foreground hover:text-yellow-400 hover:fill-yellow-400/20",
          className
        )}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label={isToolFavorite ? "Remove from favorites" : "Add to favorites"}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as any);
          }
        }}
      />
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        buttonSizeClasses[size],
        "relative group hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
        className
      )}
      onClick={handleClick}
    >
      <Star
        className={cn(
          sizeClasses[size],
          "transition-all duration-200",
          isToolFavorite
            ? "fill-yellow-400 text-yellow-400 group-hover:fill-yellow-500 group-hover:text-yellow-500"
            : "text-muted-foreground group-hover:text-yellow-400 group-hover:fill-yellow-400/20"
        )}
      />
      <span className="sr-only">
        {isToolFavorite ? "Remove from favorites" : "Add to favorites"}
      </span>
    </Button>
  )
} 